import React, { useEffect, useMemo, useState } from 'react';
import Main from '../Main';
import { Col, Form, Row } from 'react-bootstrap';
import searchIcon from '../../assets/images/Dashboard/search-icon.svg'
import calendar from '../../assets/images/Dashboard/calendar-icon.svg'
import '../../assets/css/dashboard.scss'
import '../../assets/css/customermanagement.scss'
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SbCancelBtn from '../Buttons/Buttons';
import Select from 'react-select';
import AuditTrialTable from './AuditTrialTable';
import Download from '../MainSection/Download/Download';
import { useDispatch, useSelector } from 'react-redux';
import { downloadAuditTrialDatas, loadAuditTrialData, loadModuleNameDropdownDataForSystemLogging } from '../../redux/action';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Document, Packer, Table, TableRow, TableCell, Paragraph, HeadingLevel } from "docx";
import { saveAs } from "file-saver";
import html2canvas from 'html2canvas';
import { Highlighter, Typeahead } from 'react-bootstrap-typeahead';
import { formatDate } from '../DateTimeUtils';

const AuditTrial = () => {

    let dispatch = useDispatch();

    const { getAuditTrialData = [], downloadAuditTrialData } = useSelector((state) => state.data);
    const { getModuleNameForSystemLogging } = useSelector((state) => state.data);
    const { totalDataCount } = useSelector((state) => state.data);

    const [fromDate, setFromDate] = useState(new Date());
    const [toDate, setToDate] = useState(new Date());
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showCustomerViewDetails, setShowCustomerViewDetails] = useState(false);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState("");
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedModule, setSelectedModule] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10); // Set initial rows per page
    const [excelDownloadRequested, setExcelDownloadRequested] = useState(false);
    const [pdfDownloadRequested, setPdfDownloadRequested] = useState(false);
    const [wordDownloadRequested, setWordDownloadRequested] = useState(false);
    const [printDownloadRequested, setPrintDownloadRequested] = useState(false);
    const [imageDownloadRequested, setImageDownloadRequested] = useState(false);
    const [selected, setSelected] = useState([]);

    // Add a new state for downloading
    const [isDownloading, setIsDownloading] = useState(false);

    useEffect(() => {
        dispatch(loadAuditTrialData(fromDate, toDate, searchQuery, selectedModule, rowsPerPage, page + 1, setLoading));
    }, [fromDate, toDate, searchQuery, selectedModule, rowsPerPage, page, setLoading]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    useEffect(() => {
        dispatch(loadModuleNameDropdownDataForSystemLogging());
    }, [])

    const handleModuleSelection = (selectedOption) => {
        if (selectedOption) {
            const moduleName = selectedOption.value;  // Extract StatusID (assuming 'value' is the StatusID)
            setSelectedModule(moduleName);  // Store StatusID in state
        }
    };

    const handleViewDetails = () => {
        setShowCustomerViewDetails(!showCustomerViewDetails);
    }

    const handleClearFilters = () => {
        setFromDate(null);
        setToDate(null);
        setSearchQuery("");
        setSearchText("");
        setSelectedModule("");
        setSelected([]);
    }

    // Search trigger function
    const handleSearchClick = () => {
        setSearchQuery(searchText); // Set the search query only when the button is clicked
    };

    // Handle the search input change (when selecting an item from Typeahead)
    const handleSearchChange = (selected) => {
        const query = selected.length ? selected[0] : ''; // Get the selected query or an empty string
        const selectedActivityLogId = query.ActivityLogID;
        setSearchQuery(selectedActivityLogId); // Update searchQuery
        setSearchText(query); // Update searchText
        setSelected(selected);
    };

    // Function to handle Excel download
    const handleDownloadExcel = () => {
        setIsDownloading(true);
        setExcelDownloadRequested(true);
        dispatch(downloadAuditTrialDatas(fromDate, toDate, searchQuery, selectedModule, setLoading, setIsDownloading));
    };

    useEffect(() => {
        if (excelDownloadRequested && downloadAuditTrialData && downloadAuditTrialData.length > 0) {
            const dataToExport = downloadAuditTrialData.map(item => ({
                "TIME STAMP": item.Date || "NA",
                "USER": item.UpdatedBy || "NA",
                "MODULES": item.ModuleName || "NA",
                "ACTIVITY TYPE": item.Type || "NA",
                "DESCRIPTION": item.Description || "NA",
            }));

            // Create worksheet
            const ws = XLSX.utils.json_to_sheet(dataToExport);

            // Calculate column widths
            const columnWidths = Object.keys(dataToExport[0]).map((key, colIndex) => {
                const maxLength = Math.max(
                    key.length,
                    ...dataToExport.map(row => (row[key] ? row[key].toString().length : 0))
                );
                return { wch: maxLength + 5 }; // Add padding
            });

            ws['!cols'] = columnWidths; // Set column widths

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "ActivityLog");

            // Download
            XLSX.writeFile(wb, 'activity_Log.xlsx');
            // Reset flag after download
            setExcelDownloadRequested(false); // ✅ So it doesn’t re-trigger
        }
    }, [excelDownloadRequested, downloadAuditTrialData]);

    // Function to handle PDF download
    const handleDownloadPDF = () => {
        setIsDownloading(true);
        setPdfDownloadRequested(true);
        dispatch(downloadAuditTrialDatas(fromDate, toDate, searchQuery, selectedModule, setLoading));
    };

    useEffect(() => {
        if (pdfDownloadRequested && downloadAuditTrialData && downloadAuditTrialData.length > 0) {
            const doc = new jsPDF({ orientation: 'landscape' }); // Landscape for more width

            // Title
            doc.setFontSize(18);
            doc.text("Activity Log Data", 14, 20);

            const dataToExport = downloadAuditTrialData?.map(item => ([
                item.Date || "NA",
                item.UpdatedBy || "NA",
                item.ModuleName || "NA",
                item.Type || "NA",
                item.Description || "NA",
            ])) || [];

            const columns = [
                { title: "TIME STAMP", dataKey: "timeStamp" },
                { title: "USER", dataKey: "user" },
                { title: "MODULES", dataKey: "module" },
                { title: "ACTIVITY TYPE", dataKey: "activityType" },
                { title: "DESCRIPTION", dataKey: "description" },
            ];

            // Generate table
            doc.autoTable({
                head: [columns],
                body: dataToExport,
                startY: 30,
                tableWidth: 'auto', // Auto-adjust table to fit page
                styles: {
                    fontSize: 8,
                    cellPadding: 2,
                    overflow: 'linebreak',
                },
                headStyles: {
                    fillColor: [22, 160, 133],
                    textColor: [255, 255, 255],
                    halign: 'start',
                },
            });

            // Save PDF
            doc.save('activity_log.pdf');
            // Reset flag after download
            setPdfDownloadRequested(false); // ✅ So it doesn’t re-trigger
        }
    }, [pdfDownloadRequested, downloadAuditTrialData]);

    const handleDownloadWord = () => {
        setIsDownloading(true);
        setWordDownloadRequested(true);
        dispatch(downloadAuditTrialDatas(fromDate, toDate, searchQuery, selectedModule, setLoading));
    };

    useEffect(() => {
        if (wordDownloadRequested && downloadAuditTrialData && downloadAuditTrialData.length > 0) {
            const dataToExport = downloadAuditTrialData.map(item => [
                String(item?.Date || "NA"),
                String(item?.UpdatedBy || "NA"),
                String(item?.ModuleName || "NA"),
                String(item?.Type || "NA"),
                String(item?.Description || "NA"),
            ]);

            const headers = ["TIME STAMP", "USER", "MODULES", "ACTIVITY TYPE", "DESCRIPTION"];

            const tableRows = [
                new TableRow({
                    children: headers.map(header =>
                        new TableCell({
                            children: [new Paragraph({ text: header, bold: true })],
                        })
                    ),
                }),
                ...dataToExport.map(row =>
                    new TableRow({
                        children: row.map(cell =>
                            new TableCell({
                                children: [new Paragraph(cell)],
                            })
                        ),
                    })
                ),
            ];

            // ✅ Declare `table` BEFORE using it in `sections`
            const table = new Table({
                rows: tableRows,
            });

            const doc = new Document({
                sections: [
                    {
                        properties: {},
                        children: [
                            new Paragraph({
                                text: "Activity Trial Report",
                                heading: HeadingLevel.HEADING_1,
                            }),
                            table, // Now this is defined correctly
                        ],
                    },
                ],
            });

            Packer.toBlob(doc)
                .then(blob => saveAs(blob, "activity_trial.docx"))
                .catch(error => {
                    console.error("Error generating Word document:", error);
                    alert("An error occurred while generating the Word document.");
                })
                .finally(() => {
                    setWordDownloadRequested(false);
                });
        } else if (wordDownloadRequested && (!downloadAuditTrialData || downloadAuditTrialData.length === 0)) {
            alert("No data to export!");
            setWordDownloadRequested(false);
        }
    }, [wordDownloadRequested, downloadAuditTrialData]);

    const handlePrint = () => {
        setIsDownloading(true);
        setPrintDownloadRequested(true);
        dispatch(downloadAuditTrialDatas(fromDate, toDate, searchQuery, selectedModule, setLoading));
    };

    useEffect(() => {
        if (printDownloadRequested && downloadAuditTrialData && downloadAuditTrialData.length > 0) {
            if (!downloadAuditTrialData || downloadAuditTrialData.length === 0) {
                console.error("No data available for printing.");
                alert("No data to print!");
                setPrintDownloadRequested(false);
                return;
            }

            const dataToExport = downloadAuditTrialData.map(item => [
                item.Date || "NA",
                item.UpdatedBy || "NA",
                item.ModuleName || "NA",
                item.Type || "NA",
                item.Description || "NA",
            ]);

            const headers = [
                "TIME STAMP", "USER", "MODULES", "ACTIVITY TYPE", "DESCRIPTION",
            ];

            const tableHtml = `
      <table border="1" style="border-collapse: collapse; width: 100%; font-family: Arial, sans-serif;">
        <thead>
          <tr>
            ${headers.map(header => `<th style="padding: 8px; background-color: #f2f2f2; text-align: left;">${header}</th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${dataToExport.map(row => `
            <tr>
              ${row.map(cell => `<td style="padding: 8px; text-align: left;">${cell}</td>`).join('')}
            </tr>
          `).join('')}
        </tbody>
      </table>
    `;

            const printWindow = window.open('', '', 'width=800,height=600');
            printWindow.document.write('<html><head><title>Activity Trial Report</title>');
            printWindow.document.write('<style>body { font-family: Arial, sans-serif; }</style>');
            printWindow.document.write('</head><body>');
            printWindow.document.write('<h1>Activity Trial Report</h1>');
            printWindow.document.write(tableHtml);
            printWindow.document.write('</body></html>');
            printWindow.document.close();

            // Wait for content to load before printing
            printWindow.onload = () => {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
                setPrintDownloadRequested(false);
            };
        }
    }, [printDownloadRequested, downloadAuditTrialData]);

    const handleDownloadImage = () => {
        setIsDownloading(true);
        setImageDownloadRequested(true);
        dispatch(downloadAuditTrialDatas(fromDate, toDate, searchQuery, selectedModule, setLoading));
    };

    useEffect(() => {
        if (imageDownloadRequested) {
            if (!downloadAuditTrialData || downloadAuditTrialData.length === 0) {
                console.error("No data available for exporting.");
                alert("No data to export!");
                return;
            }

            const headers = ["TIME STAMP", "USER", "MODULES", "ACTIVITY TYPE", "DESCRIPTION"];
            const chunkSize = 50; // You can increase/decrease this
            const chunks = [];

            for (let i = 0; i < downloadAuditTrialData.length; i += chunkSize) {
                chunks.push(downloadAuditTrialData.slice(i, i + chunkSize));
            }

            let currentChunk = 0;

            const processNextChunk = () => {
                if (currentChunk >= chunks.length) return;

                const dataToExport = chunks[currentChunk].map(item => [
                    item.Date || "NA",
                    item.UpdatedBy || "NA",
                    item.ModuleName || "NA",
                    item.Type || "NA",
                    item.Description || "NA",
                ]);

                const printContainer = document.createElement("div");
                printContainer.style.position = "absolute";
                printContainer.style.left = "-9999px";
                printContainer.style.backgroundColor = "#fff";
                printContainer.style.padding = "20px";
                printContainer.style.border = "1px solid #ccc";
                printContainer.style.fontFamily = "Arial, sans-serif";
                printContainer.style.width = "1000px";

                printContainer.innerHTML = `
                <h2 style="text-align: center;">Audit Trial Report (Page ${currentChunk + 1})</h2>
                <table border="1" style="border-collapse: collapse; width: 100%; table-layout: fixed; word-wrap: break-word;">
                    <thead>
                        <tr>
                            ${headers.map(header => `<th style="padding: 10px; background-color: #f2f2f2;">${header}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${dataToExport.map(row => `
                            <tr>
                                ${row.map(cell => `<td style="padding: 10px;">${cell}</td>`).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
            `;

                document.body.appendChild(printContainer);

                setTimeout(() => {
                    html2canvas(printContainer, {
                        scale: 1.5,
                        useCORS: true,
                        backgroundColor: "#fff",
                    }).then(canvas => {
                        document.body.removeChild(printContainer);
                        canvas.toBlob(blob => {
                            if (!blob) {
                                console.error("Image generation failed.");
                                alert("Failed to generate image.");
                                return;
                            }

                            const link = document.createElement("a");
                            link.href = URL.createObjectURL(blob);
                            link.download = `audit_trial_report_page_${currentChunk + 1}.png`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);

                            currentChunk++;
                            processNextChunk(); // Process next chunk
                        }, "image/png");
                    }).catch(err => {
                        console.error("Error capturing image:", err);
                        alert("Error generating image. Try again.");
                        document.body.removeChild(printContainer);
                    });
                }, 500);
            };

            processNextChunk();
        }
    }, [imageDownloadRequested, downloadAuditTrialData]);

    const processedAuditTrialData = useMemo(() => {
        return getAuditTrialData?.map(item => ({
            ...item,
            Date: String(formatDate(item.Date || 'N/A')),
            UpdatedBy: String(item.UpdatedBy || 'N/A'),
            ModuleName: String(item.ModuleName || 'N/A'),
            Type: String(item.Type || 'N/A'),
        }));
    }, [getAuditTrialData]);

    return (
        <div className="customer-management-container">
            <Main>
                <div className='components-download-container'>
                    <Download
                        showDashboardLibraryButton={false}
                        showSaveCustomViewButton={false}
                        showAnalyticsButton={false}
                        showDownloadDropdown={true}
                        onExcelDownloadClick={handleDownloadExcel}
                        onPdfDownloadClick={handleDownloadPDF}
                        onWordDownloadClick={handleDownloadWord}
                        onPrintClick={handlePrint}
                        onImageClick={handleDownloadImage}
                    />
                </div>
                <div className="customermanagement-content-container">
                    <div className="customermanagement-content">
                        <Row className='customermanagement-btns-container'>
                            <Col md={2} className='customermanagement-backbtn-div'>
                                {/* <img src={backbtn} alt="" /> */}
                                <span>Audit Trial</span>
                            </Col>
                            <Col md={10} className='customermanagement-search-filter'>
                                <Col md={4} className='filters-col'>
                                    <div className='customermanagement-search'>
                                        {/* <Form.Control
                                            type="search"
                                            placeholder='Search'
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        /> */}
                                        <Typeahead
                                            id="search-typeahead"
                                            className='typeahead-search'
                                            onChange={handleSearchChange}
                                            // options={[...new Set(complainEscalationData.map(complaint => complaint.AssignedToUser))]}
                                            options={processedAuditTrialData}
                                            labelKey="UpdatedBy" // We are using a string (the company name), so we just use the string directly
                                            filterBy={['Date', 'UpdatedBy', 'ModuleName', 'Type']}
                                            placeholder="Search Audit Trial"
                                            value={searchText}
                                            selected={selected}
                                            onInputChange={(input) => setSearchText(input)} // Update searchText with typed value
                                            minLength={1} // Minimum characters before suggestions appear
                                            autoFocus={false} // Prevent auto focusing on input
                                            renderMenu={(results, menuProps) => {
                                                // ✅ Hide table if an item is selected
                                                if (selected.length > 0 || !results.length) {
                                                    return null;
                                                }

                                                return (
                                                    <div {...menuProps} className='typeahead-search-options-table'>
                                                        <table className="table table-sm  mb-0">
                                                            <thead className="thead-light">
                                                                <tr>
                                                                    <th style={{ 'minWidth': '180px' }}>Date</th>
                                                                    <th style={{ 'minWidth': '150px' }}>User</th>
                                                                    <th style={{ 'minWidth': '180px' }}>Module</th>
                                                                    <th style={{ 'minWidth': '120px' }}>Activity type</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {results.map((option, index) => (
                                                                    <tr key={option.ActivityLogID} onClick={() => handleSearchChange([option])} style={{ cursor: 'pointer' }}>
                                                                        <td><Highlighter search={searchText}>{option.Date}</Highlighter></td>
                                                                        <td><Highlighter search={searchText}>{option.UpdatedBy}</Highlighter></td>
                                                                        <td><Highlighter search={searchText}>{option.ModuleName}</Highlighter></td>
                                                                        <td><Highlighter search={searchText}>{option.Type}</Highlighter></td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                );
                                            }}
                                        />
                                        {/* <img src={searchIcon} alt="" onClick={handleSearchClick} /> */}
                                    </div>
                                </Col>
                                <Col md={2} className='filters-col'>
                                    <Select
                                        className="react-select-container complaint-status-select"
                                        classNamePrefix="react-select"
                                        placeholder="Modules"
                                        options={getModuleNameForSystemLogging}
                                        style={{ width: "100%" }}
                                        value={selectedModule ? getModuleNameForSystemLogging.find(module => module.value === selectedModule) : null}
                                        onChange={(selectedOption) => handleModuleSelection(selectedOption)}
                                    />
                                </Col>
                                <Col md={2} className='filters-col'>
                                    <div className='calendar-container'>
                                        <DatePicker
                                            className="select from-date"
                                            id="date-range-picker"
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText='From'
                                            selected={fromDate}
                                            onChange={(date) => setFromDate(date)}
                                            maxDate={new Date()}
                                            onChangeRaw={e => e.preventDefault()}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                        />
                                        <img src={calendar} alt="" />
                                    </div>
                                </Col>
                                <Col md={2} className='filters-col'>
                                    <div className='calendar-container'>
                                        <DatePicker
                                            className="select from-date"
                                            id="date-range-picker"
                                            dateFormat="dd/MM/yyyy"
                                            placeholderText='To'
                                            selected={toDate}
                                            onChange={(date) => setToDate(date)}
                                            maxDate={new Date()}
                                            onChangeRaw={e => e.preventDefault()}
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                        />
                                        <img src={calendar} alt="" />
                                    </div>
                                </Col>
                                <SbCancelBtn btnName="Clear" onClickEffect={handleClearFilters} />
                            </Col>
                        </Row>
                        <div className='customermanagement-table-div'>
                            <AuditTrialTable
                                handleOpenModal={handleOpenModal}
                                handleViewDetails={handleViewDetails}
                                getAuditTrialData={getAuditTrialData}
                                loading={loading}
                                rowsPerPage={rowsPerPage}
                                setRowsPerPage={setRowsPerPage}
                                setPage={setPage}
                                page={page}
                                totalDataCount={totalDataCount}
                                isDownloading={isDownloading}
                            />
                        </div>
                    </div>
                </div>
            </Main>
            {/* {
                isModalOpen && <AddComplaintEsaclation
                    open={isModalOpen}
                    handleCloseModal={handleCloseModal}
                />
            } */}
            {/* {showColumnFilterModal && (
                <CustomerFilterModal
                    showColumnFilterModal={showColumnFilterModal}
                    handleCloseColumnFilterModal={handleCloseColumnFilterModal}
                />
            )}
            {showValueFilterModal && (
                <CustomerValueFilterModal
                    showValueFilterModal={showValueFilterModal}
                    handleCloseValueFilterModal={handleCloseValueFilterModal}
                />
            )}
            {showCustomerViewDetails && (
                <CustomerViewDetails
                    showCustomerViewDetails={showCustomerViewDetails}
                    handleCloseViewDetails={handleCloseViewDetails}
                />
            )} */}
        </div >
    );
};

export default AuditTrial;